import { useState } from "react"
import { CloseOutlined } from "@ant-design/icons"
import { Resizable as ResizableComponent } from "react-resizable"
import type { ResizeCallbackData } from "react-resizable"
import { cn } from 'utils/classnames'
import { Button } from 'uikit/buttons/default'
import { Flex } from 'uikit/layout/default'
import styles from './drawer.module.scss'

const Resizable = ResizableComponent as React.ComponentType<{
    width: number
    height: number
    axis?: "x"
    resizeHandles?: ["w"]
    handle?: React.ReactNode
    onResize?: (e: React.SyntheticEvent<Element>, data: ResizeCallbackData) => void
    minConstraints?: [number, number]
    maxConstraints?: [number, number]
    children: React.ReactNode
}>

export interface DrawerProps {
    children: React.ReactNode
    title: string
    isOpen: boolean
    onClose: () => void
    width?: number
    minWidth?: number
    maxWidth?: number
    className?: string
}

/**
 * A drawer component that overlays from the right with a close button in the header.
 * Used for forms and content panels within graph views.
 * Supports horizontal resizing by dragging the left edge.
 */
export function Drawer({
    children,
    title,
    isOpen,
    onClose,
    width = 410,
    minWidth = 350,
    maxWidth = 950,
    className
}: DrawerProps) {
    const [currentWidth, setCurrentWidth] = useState(width)

    const handleResize = (_e: React.SyntheticEvent, { size }: ResizeCallbackData) => {
        setCurrentWidth(size.width)
    }

    if (!isOpen) {
        return null
    }

    return (
        <Resizable
            width={currentWidth}
            height={0}
            axis="x"
            resizeHandles={["w"]}
            onResize={handleResize}
            minConstraints={[minWidth, 0]}
            maxConstraints={[maxWidth, Infinity]}
            handle={
                <div
                    className={styles.resizeHandle}
                    style={{ touchAction: "none" }}
                />
            }
        >
            <div
                className={cn(styles.drawerContainer, className)}
                style={{ width: currentWidth, boxShadow: "-4px 0 4px 0 rgba(0, 0, 0, 0.10)" }}
            >
                <Flex className={styles.drawerFlex}>
                    <Flex className={styles.drawerHeader}>
                        <span className={styles.drawerTitle}>{title}</span>
                        <Button type="text" icon={<CloseOutlined />} size="small" onClick={onClose} aria-label="Close drawer" />
                    </Flex>

                    <div className={styles.drawerBody}>{children}</div>
                </Flex>
            </div>
        </Resizable>
    )
}
